import { useEffect, useState } from "react";
import {
  FaBars,
  FaChevronDown,
  FaCrown,
  FaHome,
  FaMoon,
  FaQrcode,
  FaQuestionCircle,
  FaSignInAlt,
  FaSignOutAlt,
  FaStar,
  FaSun,
  FaTimes,
  FaUser,
  FaUserPlus
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../../api/action/user";
import { useTheme } from "../../../../context/ThemeContext.jsx";
import { useTemplateAuthState } from "../../../../template/TemplateAuthState.jsx";
import { useAuthSession } from "../../../../auth/useAuthSession.js";
import { clearKioskSession, isKioskSessionActive } from "../../../../domains/flexOffice/kiosk/kioskSession.js";
import {
  getManualPaymentDisplayDetails,
  getSelectablePaymentOptions
} from "../../../payment/paymentMethodUtils";

const FlexOfficeHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.auth?.data);
  const companyState = useSelector((state) => state.companyReducer.company);
  const templateAuth = useTemplateAuthState();
  const authSession = useAuthSession();

  // Kiosk sessions reuse this header unmodified on the booking/my-bookings pages
  // (see KIOSK_ALLOWED_PATH_PREFIXES). "My Profile" leads to /user-profile, which
  // isn't kiosk-allowed and just bounces the guest back to /kiosk, so it's hidden
  // here; Logout is rewired to end the kiosk session cleanly instead of the normal
  // sign-in flow.
  const isKiosk = isKioskSessionActive();
  const guestMode = localStorage.getItem("guestAuthenticated") === "true";
  const isAuthenticated = authSession.isAuthenticated || authSession.isLoggedIn;
  const displayName = userData?.firstName || userData?.email?.split("@")[0] || templateAuth.displayName || "Account";
  const hasUserData = Boolean(userData?.firstName || userData?.email || (templateAuth.isLoggedIn && templateAuth.displayName));
  const Email = userData?.email || templateAuth.email || "";
  const profileImageUrl = userData?.profilePicture?.displayLink || templateAuth.avatarUrl || null;
  const logoUrl = companyState?.logoImageContent?.displayLink;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const { darkMode, toggleDarkMode } = useTheme() || {};
  const companyPaymentMethods = companyState?.account?.paymentMethods || [];
  const paymentOptions = getSelectablePaymentOptions(
    companyState?.operatingLocation?.code,
    companyPaymentMethods,
    companyState
  );
  const manualPaymentOption = paymentOptions.find((option) => option.requiresReference) || null;
  const manualPaymentDetails = getManualPaymentDisplayDetails(companyState, manualPaymentOption);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lets the native app's bottom tab bar (FlexOfficeMobileTabBar.jsx) open
  // this same drawer instead of duplicating its content.
  useEffect(() => {
    const handleOpenMobileMenu = () => setIsMobileMenuOpen(true);
    window.addEventListener("flexoffice:open-mobile-menu", handleOpenMobileMenu);
    return () => window.removeEventListener("flexoffice:open-mobile-menu", handleOpenMobileMenu);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".profile-dropdown")) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsProfileDropdownOpen(false);
  };

  // A kiosk guest tapping the header's Login/Sign Up must land on the
  // kiosk's own guest-only screens (/kiosk/login, /kiosk/register), never
  // the public /sign-in /sign-up — the public sign-in has no restriction
  // against a staff/admin account signing in there, which must never happen
  // on a shared public device (see KioskLogin.jsx's role check). Identical
  // behavior for every non-kiosk visitor either way.
  const goToLogin = () => handleNavigation(isKiosk ? "/kiosk/login" : "/sign-in");
  const goToSignUp = () => handleNavigation(isKiosk ? "/kiosk/register" : "/sign-up");

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);

    if (isKiosk) {
      try {
        await clearKioskSession(dispatch);
      } finally {
        setIsLoggingOut(false);
        navigate("/kiosk", { replace: true });
      }
      return;
    }

    try {
      await dispatch(logout({ reason: "flex-header-logout" }));
      const cookiesToExpire = [
        "connect.sid",
        "refreshToken",
        "accessToken",
        "token",
        "JSESSIONID",
      ];
      cookiesToExpire.forEach((name) => {
        const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = base;
        document.cookie = `${base} domain=${window.location.hostname};`;
      });

      navigate("/sign-in", { replace: true });
    } catch {
      navigate("/sign-in", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen((v) => !v);
  const toggleProfileDropdown = () => setIsProfileDropdownOpen((v) => !v);
  const handleImageError = () => setImageError(true);

  // "Features" and "Inquire Now" aren't part of the kiosk-allowed route set
  // (KIOSK_ALLOWED_PATH_PREFIXES) and just bounce a kiosk guest back to
  // /kiosk, same as "My Profile" above — hidden for kiosk sessions. "Home"
  // is kept but repointed at /kiosk instead of "/" for the same reason.
  const navItems = isKiosk
    ? [{ id: "nav-home", label: "Home", path: "/kiosk", icon: FaHome }]
    : [
        { id: "nav-home", label: "Home", path: "/", icon: FaHome },
        { id: "nav-features", label: "Features", path: "/our-features", icon: FaStar },
        {
          id: "contact-us",
          label: "Inquire Now",
          path: "/contact",
          icon: FaQuestionCircle,
          highlight: true,
        },
      ];

  const guestUser = {
    firstName: hasUserData ? displayName : "Admin",
    email: Email || "Admin",
  };

  return (
    <header
      className={`fixed safe-area-top left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? "bg-white shadow-lg border-b border-slate-200"
        : "bg-white border-b border-slate-100"
        }`}
      style={{ fontFamily: "var(--flex-font, 'Inter', sans-serif)" }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        @media (max-width: 640px) {
          .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">

          {/* ── Logo ── */}
          <div
            className="flex items-center cursor-pointer group flex-shrink-0"
            onClick={() => handleNavigation(isKiosk ? "/kiosk" : "/")}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="CoworkFlex Logo"
                className="h-8 sm:h-10 lg:h-12 w-auto max-w-[100px] sm:max-w-[120px] lg:max-w-32 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="h-8 sm:h-10 lg:h-12 px-3 sm:px-4 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-lg sm:rounded-xl flex items-center justify-center font-bold text-white text-sm sm:text-base lg:text-lg transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg shadow-md">
                {companyState?.name || "FlexOffice"}
              </div>
            )}
          </div>

          {/* ── Desktop Nav ── */}
          <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
            {navItems.map((item) => (
              <button
                id={item.id}
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center space-x-1 xl:space-x-2 px-3 xl:px-5 py-2 xl:py-2.5 rounded-xl transition-all duration-300 font-semibold text-xs xl:text-sm ${item.highlight
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transform hover:scale-105"
                  : "text-slate-700 hover:text-blue-600 hover:bg-blue-50"
                  }`}
              >
                <item.icon className="w-3 h-3 xl:w-4 xl:h-4" />
                <span>{item.label}</span>
              </button>
            ))}
            {manualPaymentOption && (
              <button
                type="button"
                onClick={() => setShowZelleModal(true)}
                className="flex items-center space-x-1 xl:space-x-2 px-3 xl:px-5 py-2 xl:py-2.5 rounded-xl transition-all duration-300 font-semibold text-xs xl:text-sm text-violet-700 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 border border-violet-200 hover:border-violet-300"
              >
                <FaQrcode className="w-3 h-3 xl:w-4 xl:h-4" />
                <span>{manualPaymentDetails.qrImageUrl ? `${manualPaymentDetails.title} QR` : manualPaymentDetails.title}</span>
              </button>
            )}
            {!isKiosk && (
              <button
                type="button"
                onClick={toggleDarkMode}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? <FaSun className="h-4 w-4" /> : <FaMoon className="h-4 w-4" />}
              </button>
            )}
          </nav>

          {/* ── Desktop Auth ── */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
            {guestMode ? (
              /* Guest / Admin-viewing-as-guest */
              <div className="relative profile-dropdown">
                <button
                  id="nav-guest-profile"
                  onClick={toggleProfileDropdown}
                  className="flex items-center space-x-1 xl:space-x-2 px-3 xl:px-4 py-2 xl:py-2.5 rounded-xl transition-all duration-300 text-amber-700 hover:text-amber-600 hover:bg-gradient-to-r hover:from-amber-50 hover:to-yellow-50 border border-amber-200 hover:border-amber-300 shadow-sm hover:shadow-md"
                >
                  <div className="w-6 h-6 xl:w-8 xl:h-8 rounded-full flex items-center justify-center border-2 border-amber-200 overflow-hidden bg-gradient-to-br from-amber-100 to-yellow-100 transition-all duration-300 hover:border-amber-400">
                    <FaCrown className="w-3 h-3 xl:w-4 xl:h-4 text-amber-600" />
                  </div>
                  <span className="font-semibold text-xs xl:text-sm max-w-16 xl:max-w-24 truncate">
                    {guestUser.firstName}
                  </span>
                  <FaChevronDown
                    className={`w-2 h-2 xl:w-3 xl:h-3 transition-transform duration-300 ${isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {isProfileDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 xl:w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 overflow-hidden animate-fadeIn">
                    <div className="px-3 xl:px-4 py-2 xl:py-3 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-yellow-50">
                      <div className="flex items-center space-x-2">
                        <FaCrown className="w-3 h-3 xl:w-4 xl:h-4 text-amber-600" />
                        <p className="font-semibold text-amber-800 text-xs xl:text-sm truncate">
                          {guestUser.firstName}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-1">
                        Viewing as Administrator
                      </p>
                    </div>
                    <button
                      id="desktop-logout-button"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="w-full flex items-center space-x-2 xl:space-x-3 px-3 xl:px-4 py-2 xl:py-3 text-red-600 hover:bg-red-50 transition-all duration-200 group disabled:opacity-60"
                    >
                      <FaSignOutAlt className="w-3 h-3 xl:w-4 xl:h-4 group-hover:scale-110 transition-transform duration-200" />
                      <span className="font-medium text-xs xl:text-sm">
                        {isLoggingOut ? "Logging out..." : "Logout"}
                      </span>
                    </button>
                  </div>
                )}
              </div>

            ) : isAuthenticated && hasUserData ? (
              /* Authenticated User */
              <div className="relative profile-dropdown">
                <button
                  id="nav-profile"
                  onClick={toggleProfileDropdown}
                  className="flex items-center space-x-1 xl:space-x-2 px-3 xl:px-4 py-2 xl:py-2.5 rounded-xl transition-all duration-300 text-slate-700 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-md"
                >
                  <div
                    className="w-6 h-6 xl:w-8 xl:h-8 rounded-full flex items-center justify-center border-2 border-slate-200 overflow-hidden transition-all duration-300 hover:border-blue-400"
                    style={{ background: 'linear-gradient(135deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)' }}
                  >
                    {profileImageUrl && !imageError ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={handleImageError}
                      />
                    ) : (
                      <FaUser className="w-3 h-3 xl:w-4 xl:h-4 text-blue-600" />
                    )}
                  </div>
                  <span className="font-semibold text-xs xl:text-sm max-w-16 xl:max-w-24 truncate">
                    {displayName}
                  </span>
                  <FaChevronDown
                    className={`w-2 h-2 xl:w-3 xl:h-3 transition-transform duration-300 ${isProfileDropdownOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {isProfileDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 xl:w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 overflow-hidden animate-fadeIn">
                    <div
                      className="px-3 xl:px-4 py-2 xl:py-3 border-b border-slate-200"
                      style={{ background: 'linear-gradient(90deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)' }}
                    >
                      <p className="font-semibold text-slate-800 text-xs xl:text-sm truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {Email}
                      </p>
                    </div>

                    {!isKiosk && (
                      <>
                        <button
                          id="desktop-my-profile-button"
                          onClick={() => handleNavigation("/user-profile")}
                          className="w-full flex items-center space-x-2 xl:space-x-3 px-3 xl:px-4 py-2 xl:py-3 text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200 group"
                        >
                          <FaUser className="w-3 h-3 xl:w-4 xl:h-4 text-slate-600 group-hover:text-blue-600 transition-colors duration-200" />
                          <span className="font-medium text-xs xl:text-sm">My Profile</span>
                        </button>

                        <hr className="my-1 border-slate-200" />
                      </>
                    )}

                    <button
                      id="desktop-logout-button"
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="w-full flex items-center space-x-2 xl:space-x-3 px-3 xl:px-4 py-2 xl:py-3 text-red-600 hover:bg-red-50 transition-all duration-200 group disabled:opacity-60"
                    >
                      <FaSignOutAlt className="w-3 h-3 xl:w-4 xl:h-4 group-hover:scale-110 transition-transform duration-200" />
                      <span className="font-medium text-xs xl:text-sm">
                        {isLoggingOut ? "Logging out..." : "Logout"}
                      </span>
                    </button>
                  </div>
                )}
              </div>

            ) : (
              /* Not Authenticated */
              <div className="flex items-center space-x-2 xl:space-x-3">
                <button
                  id="desktop-login-button"
                  onClick={goToLogin}
                  className="flex items-center space-x-1 xl:space-x-2 px-3 xl:px-5 py-2 xl:py-2.5 rounded-xl font-semibold text-xs xl:text-sm text-slate-700 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <FaSignInAlt className="w-3 h-3 xl:w-4 xl:h-4" />
                  <span>Login</span>
                </button>
                <button
                  id="signup-button"
                  onClick={goToSignUp}
                  className="flex items-center space-x-1 xl:space-x-2 px-3 xl:px-5 py-2 xl:py-2.5 rounded-xl font-semibold text-xs xl:text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                >
                  <FaUserPlus className="w-3 h-3 xl:w-4 xl:h-4" />
                  <span>Sign Up</span>
                </button>
              </div>
            )}
          </div>

          {/* ── Mobile Toggle: hamburger before login, profile icon after ── */}
          {isMobileMenuOpen || !((guestMode) || (isAuthenticated && hasUserData)) ? (
            <button
              id="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 sm:p-2.5 rounded-xl transition-all duration-300 text-slate-700 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 shadow-sm hover:shadow-md"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <FaBars className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
          ) : (
            <button
              id="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 overflow-hidden shadow-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              style={
                guestMode
                  ? { borderColor: '#fde68a', background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)' }
                  : { borderColor: '#bfdbfe', background: 'linear-gradient(135deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)' }
              }
              aria-label="Open account menu"
            >
              {guestMode ? (
                <FaCrown className="w-4 h-4 text-amber-600" />
              ) : profileImageUrl && !imageError ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              ) : (
                <FaUser className="w-4 h-4 text-blue-600" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      <div
        className={`lg:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen
          ? "max-h-screen opacity-100 visible"
          : "max-h-0 opacity-0 invisible overflow-hidden"
          }`}
      >
        <div className="bg-gradient-to-b from-white to-slate-50 border-t border-slate-200 shadow-xl">
          <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-1 sm:space-y-2">

            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-all duration-300 font-semibold text-sm ${item.highlight
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                  : "text-slate-700 hover:text-blue-600 hover:bg-blue-50"
                  }`}
              >
                <item.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">{item.label}</span>
              </button>
            ))}

            {manualPaymentOption && (
              <button
                type="button"
                onClick={() => {
                  setShowZelleModal(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 transition-all duration-200"
              >
                <FaQrcode className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-semibold text-sm">
                  {manualPaymentDetails.qrImageUrl ? `Open ${manualPaymentDetails.title} QR` : `Open ${manualPaymentDetails.title}`}
                </span>
              </button>
            )}

            {!isKiosk && (
              <button
                type="button"
                onClick={toggleDarkMode}
                className="w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 rounded-xl border border-slate-200 bg-white text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                <span className="font-semibold text-sm">{darkMode ? "Light mode" : "Dark mode"}</span>
                {darkMode ? <FaSun className="w-4 h-4 sm:w-5 sm:h-5" /> : <FaMoon className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            )}

            <hr className="my-2 sm:my-3 border-slate-200" />

            {guestMode ? (
              /* Mobile Guest */
              <div className="space-y-2">
                <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl mb-2 border border-amber-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 border-amber-200 overflow-hidden bg-gradient-to-br from-amber-100 to-yellow-100">
                      <FaCrown className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-amber-800 text-sm truncate">
                        {guestUser.firstName}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Viewing as Administrator
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  id="mobile-logout-button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200 disabled:opacity-60"
                >
                  <FaSignOutAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm">
                    {isLoggingOut ? "Logging out..." : "Logout"}
                  </span>
                </button>
              </div>

            ) : isAuthenticated && hasUserData ? (
              /* Mobile Authenticated */
              <div className="space-y-2">
                <div
                  className="px-3 sm:px-4 py-2 sm:py-3 rounded-xl mb-2 border border-blue-100"
                  style={{ background: 'linear-gradient(90deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)' }}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 border-blue-200 overflow-hidden"
                      style={{ background: 'linear-gradient(135deg, var(--flex-primary-soft, rgba(37, 99, 235, 0.16)) 0%, var(--flex-accent-soft, rgba(20, 184, 166, 0.14)) 100%)' }}
                    >
                      {profileImageUrl && !imageError ? (
                        <img
                          src={profileImageUrl}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={handleImageError}
                        />
                      ) : (
                        <FaUser className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">
                        {displayName}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {Email}
                      </p>
                    </div>
                  </div>
                </div>

                {!isKiosk && (
                  <button
                    id="mobile-my-profile-button"
                    onClick={() => handleNavigation("/user-profile")}
                    className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
                  >
                    <FaUser className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-semibold text-sm">My Profile</span>
                  </button>
                )}

                <button
                  id="mobile-logout-button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200 disabled:opacity-60"
                >
                  <FaSignOutAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm">
                    {isLoggingOut ? "Logging out..." : "Logout"}
                  </span>
                </button>
              </div>

            ) : (
              /* Mobile Not Authenticated */
              <div className="space-y-2">
                <button
                  id="mobile-login-button"
                  onClick={goToLogin}
                  className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-slate-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
                >
                  <FaSignInAlt className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm">Login</span>
                </button>
                <button
                  id="mobile-signup-button"
                  onClick={goToSignUp}
                  className="w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                >
                  <FaUserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-sm">Sign Up</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>


      {showZelleModal && manualPaymentOption && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-white border border-slate-100 shadow-2xl overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">Z</div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{manualPaymentDetails.title}</h3>
                  <p className="text-xs text-slate-400">Scan or enter details to complete</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowZelleModal(false)}
                className="w-7 h-7 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              >
                <FaTimes className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Body — two columns */}
            <div className="grid grid-cols-2 gap-0 divide-x divide-slate-100">

              {/* Left — QR */}
              <div className="flex flex-col items-center justify-center px-6 py-6 bg-blue-50">
                <div className="w-44 h-44 rounded-2xl bg-white border border-blue-100 p-2 shadow-sm overflow-hidden">
                  {manualPaymentDetails.qrImageUrl ? (
                    <img
                      src={manualPaymentDetails.qrImageUrl}
                      alt={`${manualPaymentDetails.title} QR code`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-center text-sm text-slate-500">
                      Use the saved recipient details to complete the payment.
                    </div>
                  )}
                </div>
                <p className="mt-4 text-xs text-blue-600 text-center font-medium leading-relaxed">
                  Scan with your banking app
                </p>
                <p className="mt-1 text-[11px] text-slate-400 text-center leading-relaxed">
                  or use the recipient details on the right
                </p>
              </div>

              {/* Right — Details */}
              <div className="flex flex-col px-6 py-6 gap-4">

                {/* Recipient info */}
                <div className="rounded-xl bg-blue-600 px-4 py-3 text-white">
                  <p className="text-[9px] uppercase tracking-widest text-blue-200 mb-1">Recipient</p>
                  <p className="text-sm font-semibold leading-tight">{manualPaymentDetails.recipientName}</p>
                  <p className="text-xs text-blue-200 mt-0.5">{manualPaymentDetails.referenceLabel}</p>
                </div>

                {/* Info tiles */}
                <div className="flex flex-col gap-2">
                  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">Use for</p>
                    <p className="text-xs font-semibold text-slate-900 mt-0.5">Walk-in payments</p>
                  </div>
                  
                </div>

                {/* Steps */}
                <div className="space-y-2">
                  {[
                    'Open your payment app or banking app.',
                    manualPaymentDetails.qrImageUrl ? 'Scan QR or enter recipient details.' : 'Use the saved recipient details.',
                    'Send amount with booking number as memo.',
                    'Confirm payment at the front desk.',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-4 h-4 mt-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <p className="text-xs text-slate-500 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60">
              <button
                type="button"
                onClick={() => setShowZelleModal(false)}
                className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-slate-800 transition"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}




    </header>
  );
};

export default FlexOfficeHeader;
